import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  Group,
  HalfFloatType,
  InstancedBufferAttribute,
  InstancedMesh,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  MeshBasicNodeMaterial,
  NoBlending,
  Object3D,
  OrthographicCamera,
  PlaneGeometry,
  RenderTarget,
  Scene,
  Vector2,
  DynamicDrawUsage,
  type WebGPURenderer,
} from 'three/webgpu';
import { mix, texture, uniform, uv, vec2, vec3 } from 'three/tsl';
import type GUI from 'lil-gui';

import { setInstanceCount } from '../instancing';
import { fbm3 } from '../noise';
import type { QualityTier } from '../studio';
import type { BackgroundContext, BackgroundScene } from './types';

interface DriftSettings {
  /** Average particles per second, before gusting. */
  spawnRate: number;
  /** Width of the spawn column in world units, and its offset from the left edge. */
  spawnBand: number;
  spawnInset: number;
  /** Noise feature size and drift for the vertical clustering field. */
  clusterScale: number;
  clusterDrift: number;
  /**
   * Candidate heights sampled per spawn, keeping the densest. 1 is a uniform
   * spray; higher values tighten the bundles.
   */
  clusterBias: number;
  /** Depth of the gusting on the spawn rate. 0 is a metronome. */
  burstAmount: number;
  burstDrift: number;
  colorNear: string;
  colorFar: string;
  colorShift: number;
  colorScale: number;

  noiseScale: number;
  noiseDrift: number;
  turns: number;
  /**
   * Constant bias added to the flow heading. Pure curl traps particles near the
   * line they were born on — everything spawns in a thin column at the left
   * edge, so without a bias the trails wind up in a vortex against it instead
   * of describing motion across the viewport.
   */
  windX: number;
  windY: number;
  force: number;
  damping: number;
  maxSpeed: number;
  lifetime: number;
  stampSize: number;
  stampStretch: number;
  stampStrength: number;
  hotColor: string;
  hotMix: number;
  trailLife: number;
  trailFloor: number;
  trailOpacity: number;
  pointerRadius: number;
  pointerPush: number;
  /**
   * Attraction never aims a particle at the cursor. It blends the flow heading
   * toward a tangential orbit, so particles wheel around the pointer and keep
   * their idle character instead of collapsing into a point.
   */
  pointerAttract: boolean;
  orbitSpeed: number;
  /** Slight inward lean on the orbit. At 0 particles spiral away and never circle. */
  orbitPull: number;
  tintColor: string;
  tintGain: number;
  tintAmount: number;
  tintRadius: number;
}

/** Tuned in the dev panel and exported 2026-08-04. */
const DESKTOP_SETTINGS: DriftSettings = {
  spawnRate: 42,
  spawnBand: 0.3,
  spawnInset: 0.195,
  clusterScale: 2.2,
  clusterDrift: 0.25,
  clusterBias: 4,
  burstAmount: 0.7,
  burstDrift: 0.35,
  colorNear: '#94008f',
  colorFar: '#493696',
  colorShift: 0.54,
  colorScale: 1.45,

  noiseScale: 2.82,
  noiseDrift: 0.16,
  turns: 2,
  windX: 0.6,
  windY: 0,
  force: 0.5,
  damping: 0.95,
  maxSpeed: 0.4,
  lifetime: 18,
  stampSize: 0.006,
  stampStretch: 1,
  stampStrength: 0.45,
  hotColor: '#8fe8ff',
  hotMix: 0.4,
  trailLife: 4.4,
  trailFloor: 0.008,
  trailOpacity: 0.32,
  pointerRadius: 0.51,
  pointerPush: 4.25,
  pointerAttract: true,
  orbitSpeed: 1.4,
  orbitPull: 1.89,
  tintColor: '#00ff91',
  tintGain: 6,
  tintAmount: 1,
  tintRadius: 0.38,
};

/**
 * Not tuned by hand — desktop's values with the three that have to differ on a
 * half-resolution trail buffer and a smaller screen. Tune with
 * `?quality=mobile` plus a reload; the tier is read once at construction.
 */
const MOBILE_SETTINGS: DriftSettings = {
  ...DESKTOP_SETTINGS,
  spawnRate: 24,
  stampSize: 0.009,
  trailLife: 3.4,
};

/** Headroom for the top of the `spawnRate` × `lifetime` range, not a target. */
const DESKTOP_MAX_PARTICLES = 4000;
const MOBILE_MAX_PARTICLES = 1200;

/**
 * Longest edge of the trail buffer in device pixels. Two half-float RGBA
 * targets at 2048×1152 are ~38 MB; without a cap a 4K display at
 * `devicePixelRatio` 2 would ask for eight times that, and the trails are soft
 * enough that the resampling is invisible.
 */
const MAX_TRAIL_EDGE = 2048;
const DESKTOP_TRAIL_SCALE = 0.75;
const MOBILE_TRAIL_SCALE = 0.5;

/**
 * Reduced-motion pre-roll, in 1/30 s steps: ~10 s of CPU-only warm-up to spread
 * particles across the field, then ~8 s (roughly twice `trailLife`) with the
 * trail passes so the buffer is saturated when the single frame is drawn.
 */
const PREROLL_WARM_STEPS = 300;
const PREROLL_TRAIL_STEPS = 240;

interface FlowParticle {
  x: number;
  y: number;
  px: number;
  py: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  r: number;
  g: number;
  b: number;
}

/**
 * A soft round brush. Stamps are additive, so the falloff is what makes
 * overlapping passes accumulate into a smooth path rather than a chain of
 * hard-edged dots.
 */
function createBrushTexture(): CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.4, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);

  const map = new CanvasTexture(canvas);
  map.needsUpdate = true;
  return map;
}

/**
 * A noise flow field seeded from the left edge, drawn as accumulated trails.
 *
 * Particles spawn in a narrow column at the left of the frustum at clustered
 * heights, then advect through the field. Only a few hundred are alive at once,
 * which is why the whole simulation runs on the CPU: at this count compute buys
 * nothing, and — unlike `flow` — the scene works on the WebGL2 fallback.
 *
 * **Spawning is deliberately not uniform.** An even spray reads as a hose. Two
 * noise fields restore the structure the cellular automaton used to supply:
 * heights are importance-sampled so emission arrives in bundles with gaps
 * between them, and the rate gusts over time. A third gives each particle a
 * colour from the near→far ramp that varies smoothly with height, so a bundle
 * shares a tone instead of being confetti.
 *
 * Each particle stamps into a half-float buffer that decays a little every
 * frame, so what reads on screen is the accumulated paths. The buffer
 * ping-pongs between two targets: decaying in place would need custom blend
 * factors, which are far less certain across both backends than one extra
 * fullscreen pass.
 */
export class DriftBackground implements BackgroundScene {
  readonly object = new Group();

  private readonly settings: DriftSettings;
  private readonly quality: QualityTier;
  private guiFolder: GUI | null = null;

  private readonly maxParticles: number;
  private readonly particles: FlowParticle[] = [];
  private readonly pool: FlowParticle[] = [];
  private elapsed = 0;
  private spawnBudget = 0;
  private prerolled = false;

  private readonly trailScale: number;
  private readonly targets: [RenderTarget, RenderTarget];
  private src = 0;
  private readonly trailScene = new Scene();
  private readonly trailCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);

  private readonly stampMesh: InstancedMesh;
  private readonly stampColors: InstancedBufferAttribute;
  private readonly brush: CanvasTexture;
  private readonly decayMesh: Mesh;
  private readonly decayMaterials: [MeshBasicNodeMaterial, MeshBasicNodeMaterial];
  private readonly compositeMesh: Mesh;
  private readonly compositeMaterials: [MeshBasicNodeMaterial, MeshBasicNodeMaterial];

  private readonly uDecay = uniform(0.98);
  private readonly uFloor = uniform(0.004);
  private readonly uTrailOpacity = uniform(1);
  private readonly uAspect = uniform(1);
  private readonly uPointer = uniform(new Vector2(0, 0));
  private readonly uTintColor = uniform(new Color());
  private readonly uTintGain = uniform(1);
  private readonly uTintAmount = uniform(1);
  private readonly uTintRadius = uniform(1);

  private readonly tmpObject = new Object3D();
  private readonly hotColor = new Color();
  private readonly nearColor = new Color();
  private readonly farColor = new Color();
  private aspect = 1;
  private pointerX = 0;
  private pointerY = 0;
  private pointerActive = false;
  private readonly reducedMotion: boolean;

  constructor(ctx: BackgroundContext) {
    this.quality = ctx.quality;
    this.reducedMotion = ctx.reducedMotion;
    this.settings = { ...(ctx.quality === 'mobile' ? MOBILE_SETTINGS : DESKTOP_SETTINGS) };
    this.maxParticles = ctx.quality === 'mobile' ? MOBILE_MAX_PARTICLES : DESKTOP_MAX_PARTICLES;
    this.trailScale = ctx.quality === 'mobile' ? MOBILE_TRAIL_SCALE : DESKTOP_TRAIL_SCALE;
    this.hotColor.set(this.settings.hotColor);
    this.nearColor.set(this.settings.colorNear);
    this.farColor.set(this.settings.colorFar);
    this.syncTintUniforms();

    // Full-viewport candidate: content sits over the whole scene on its panel
    // rather than being inset, so it is judged on the same terms as `signal`
    // and `flow`.
    ctx.onLayoutChange?.(0);

    this.targets = [createTrailTarget(), createTrailTarget()];

    this.brush = createBrushTexture();
    this.stampMesh = new InstancedMesh(
      new PlaneGeometry(1, 1),
      new MeshBasicMaterial({
        map: this.brush,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
      this.maxParticles,
    );
    this.stampMesh.frustumCulled = false;
    this.stampMesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.stampColors = new InstancedBufferAttribute(new Float32Array(this.maxParticles * 3), 3);
    this.stampColors.setUsage(DynamicDrawUsage);
    this.stampMesh.instanceColor = this.stampColors;
    setInstanceCount(this.stampMesh, 0, this.tmpObject);
    this.stampMesh.instanceMatrix.needsUpdate = true;

    const decayNode = (index: number): MeshBasicNodeMaterial => {
      const material = new MeshBasicNodeMaterial({ depthTest: false, depthWrite: false });
      material.blending = NoBlending;
      // Subtracting a floor as well as scaling matters: a pure multiply in
      // half-float approaches zero without reaching it, leaving a permanent
      // haze wherever a trail has ever been.
      material.colorNode = texture(this.targets[index]!.texture, uv())
        .rgb.mul(this.uDecay)
        .sub(this.uFloor)
        .max(0);
      return material;
    };
    this.decayMaterials = [decayNode(0), decayNode(1)];
    this.decayMesh = new Mesh(new PlaneGeometry(1, 1), this.decayMaterials[0]);
    this.decayMesh.frustumCulled = false;
    this.decayMesh.renderOrder = -1;

    this.trailScene.add(this.decayMesh);
    this.trailScene.add(this.stampMesh);

    const compositeNode = (index: number): MeshBasicNodeMaterial => {
      const material = new MeshBasicNodeMaterial({
        transparent: true,
        depthTest: false,
        depthWrite: false,
      });
      material.blending = AdditiveBlending;

      /*
       * Recolour the accumulated buffer inside the pointer's radius rather than
       * only tinting particles that happen to be there. Trails already laid down
       * light up as the cursor sweeps over them, which is what makes the pointer
       * legible even where nothing is currently being emitted. It costs one
       * distance and one mix per fragment in a pass that already runs.
       *
       * The quad spans the frustum exactly, so its UV maps linearly onto world
       * space and the pointer can be compared against it directly.
       */
      const world = uv().sub(0.5).mul(vec2(this.uAspect.mul(2), 2));
      const proximity = world.sub(this.uPointer).length().div(this.uTintRadius).oneMinus().saturate();
      const tint = mix(
        vec3(1, 1, 1),
        this.uTintColor.mul(this.uTintGain),
        proximity.pow(2).mul(this.uTintAmount),
      );

      material.colorNode = texture(this.targets[index]!.texture, uv()).rgb.mul(tint);
      material.opacityNode = this.uTrailOpacity;
      return material;
    };
    this.compositeMaterials = [compositeNode(0), compositeNode(1)];
    this.compositeMesh = new Mesh(new PlaneGeometry(1, 1), this.compositeMaterials[0]);
    this.compositeMesh.frustumCulled = false;
    this.compositeMesh.renderOrder = -1;
    this.object.add(this.compositeMesh);
  }

  /**
   * Converts elapsed time into whole spawns, gusting the rate so emission
   * arrives in surges rather than at a fixed cadence. The fractional remainder
   * carries across frames, so the average rate is independent of framerate.
   */
  private updateEmitter(dt: number): void {
    const s = this.settings;
    const gust = 1 + s.burstAmount * (fbm3(this.elapsed * s.burstDrift, 11.3, 0) * 2 - 1);
    this.spawnBudget += s.spawnRate * Math.max(0, gust) * dt;

    let guard = 64;
    while (this.spawnBudget >= 1 && guard-- > 0) {
      this.spawnBudget -= 1;
      this.spawnOne();
    }
  }

  private spawnOne(): void {
    const s = this.settings;
    const t = this.elapsed * s.clusterDrift;

    /*
     * Importance sampling rather than rejection: take a few candidate heights
     * and keep whichever sits in the densest part of the clustering field. It
     * always terminates, and the bias strength is a plain integer knob — one
     * candidate is a uniform spray, more tightens the bundles.
     */
    let y = Math.random() * 2 - 1;
    let best = -1;
    for (let i = 0, tries = Math.max(1, Math.round(s.clusterBias)); i < tries; i++) {
      const candidate = Math.random() * 2 - 1;
      const density = fbm3(candidate * s.clusterScale, t, 0);
      if (density > best) {
        best = density;
        y = candidate;
      }
    }

    const x = -this.aspect + s.spawnInset + Math.random() * s.spawnBand;
    // Sampled against height so neighbours share a tone — a bundle reads as one
    // stream rather than as confetti.
    const shade = fbm3(y * s.colorScale, t * 0.5, 7.3) * s.colorShift;

    this.emit(
      x,
      y,
      this.nearColor.r + (this.farColor.r - this.nearColor.r) * shade,
      this.nearColor.g + (this.farColor.g - this.nearColor.g) * shade,
      this.nearColor.b + (this.farColor.b - this.nearColor.b) * shade,
    );
  }

  private emit(x: number, y: number, r: number, g: number, b: number): void {
    if (this.particles.length >= this.maxParticles) return;

    const particle = this.pool.pop() ?? {
      x: 0, y: 0, px: 0, py: 0, vx: 0, vy: 0, age: 0, life: 0, r: 0, g: 0, b: 0,
    };

    particle.x = x;
    particle.y = y;
    particle.px = x;
    particle.py = y;
    particle.vx = 0;
    particle.vy = 0;
    particle.age = 0;
    particle.life = this.settings.lifetime * (0.6 + Math.random() * 0.8);
    particle.r = r;
    particle.g = g;
    particle.b = b;

    this.particles.push(particle);
  }

  update(elapsed: number, dt: number): void {
    this.elapsed = elapsed;

    // Clamp so a backgrounded tab does not resume with one enormous step.
    const step = Math.min(dt, 1 / 30);
    if (step > 0) {
      this.updateEmitter(step);
      this.advance(step);
    }
    this.writeStamps();

    this.uDecay.value = Math.exp(-step / Math.max(this.settings.trailLife, 0.01));
    this.uFloor.value = this.settings.trailFloor * step;
  }

  private advance(dt: number): void {
    const s = this.settings;
    const t = this.elapsed * s.noiseDrift;
    const turnScale = Math.PI * 2 * s.turns;
    const bound = this.aspect + 0.1;

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.age += dt;

      const dead =
        p.age >= p.life || Math.abs(p.x) > bound || Math.abs(p.y) > 1.1;
      if (dead) {
        const last = this.particles.pop()!;
        if (i < this.particles.length) this.particles[i] = last;
        this.pool.push(p);
        continue;
      }

      /*
       * Two decorrelated noise samples — one for heading, one for a speed
       * multiplier — read as curl without finite-differencing a potential
       * field. Same construction as the `flow` candidate's compute pass.
       */
      const nx = p.x * s.noiseScale;
      const ny = p.y * s.noiseScale;
      const angle = fbm3(nx, ny, t) * turnScale;
      const speed = fbm3(nx + 53.7, ny + 17.3, t) * 0.5 + 0.75;

      const idleAx = Math.cos(angle) * speed + s.windX;
      const idleAy = Math.sin(angle) * speed + s.windY;
      let ax = idleAx;
      let ay = idleAy;

      if (this.pointerActive) {
        const dx = p.x - this.pointerX;
        const dy = p.y - this.pointerY;
        const distance = Math.hypot(dx, dy);

        if (distance > 1e-5 && distance < s.pointerRadius) {
          const proximity = 1 - distance / s.pointerRadius;

          if (s.pointerAttract) {
            const rx = dx / distance;
            const ry = dy / distance;
            // Tangent to the circle around the pointer, leaning slightly inward
            // so the orbit closes instead of spiralling off.
            const orbitX = -ry * s.orbitSpeed - rx * s.orbitPull;
            const orbitY = rx * s.orbitSpeed - ry * s.orbitPull;

            // A blend, not a sum: the weight is capped below 1 so the flow noise
            // is never fully overridden and the motion stays continuous with the
            // particle's idle behaviour.
            const weight = Math.min(1, proximity * proximity * s.pointerPush);
            ax = idleAx + (orbitX - idleAx) * weight;
            ay = idleAy + (orbitY - idleAy) * weight;
          } else {
            const shove = (proximity * proximity * s.pointerPush) / distance;
            ax += dx * shove;
            ay += dy * shove;
          }
        }
      }

      p.vx = p.vx * s.damping + ax * s.force * dt;
      p.vy = p.vy * s.damping + ay * s.force * dt;

      const overspeed = Math.hypot(p.vx, p.vy) / s.maxSpeed;
      if (overspeed > 1) {
        p.vx /= overspeed;
        p.vy /= overspeed;
      }

      p.px = p.x;
      p.py = p.y;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
  }

  /**
   * One quad per particle, stretched along the step it just took. Stamping the
   * whole segment rather than the endpoint is what keeps trails continuous when
   * the framerate drops — otherwise a fast particle leaves a dotted line.
   */
  private writeStamps(): void {
    const s = this.settings;
    const colors = this.stampColors.array as Float32Array;
    let idx = 0;

    for (const p of this.particles) {
      const t = p.life > 0 ? p.age / p.life : 1;
      const envelope = Math.min(1, t / 0.1) * Math.min(1, (1 - t) / 0.33);
      if (envelope <= 0) continue;

      const dx = p.x - p.px;
      const dy = p.y - p.py;
      const travel = Math.hypot(dx, dy);

      this.tmpObject.position.set(p.px + dx * 0.5, p.py + dy * 0.5, 0);
      this.tmpObject.rotation.set(0, 0, travel > 1e-6 ? Math.atan2(dy, dx) : 0);
      this.tmpObject.scale.set(s.stampSize + travel * s.stampStretch, s.stampSize, 1);
      this.tmpObject.updateMatrix();
      this.stampMesh.setMatrixAt(idx, this.tmpObject.matrix);

      const heat = Math.min(1, Math.hypot(p.vx, p.vy) / s.maxSpeed) * s.hotMix;
      const gain = envelope * s.stampStrength;
      colors[idx * 3] = (p.r + (this.hotColor.r - p.r) * heat) * gain;
      colors[idx * 3 + 1] = (p.g + (this.hotColor.g - p.g) * heat) * gain;
      colors[idx * 3 + 2] = (p.b + (this.hotColor.b - p.b) * heat) * gain;
      idx++;
    }

    setInstanceCount(this.stampMesh, idx, this.tmpObject);
    this.stampMesh.instanceMatrix.needsUpdate = true;
    this.stampColors.needsUpdate = true;
  }

  /**
   * Not a compute pass, but this is the hook that runs after `update` and
   * before the studio's `renderer.render` — which is exactly where the trail
   * buffer has to be advanced for the composite quad to show this frame's work.
   * It is also the first hook that runs with both a renderer and a real
   * viewport size, which is what the reduced-motion pre-roll needs.
   */
  compute(renderer: WebGPURenderer): void {
    if (this.reducedMotion && !this.prerolled) {
      this.prerolled = true;
      this.preroll(renderer);
    }
    this.advanceTrail(renderer);
  }

  private advanceTrail(renderer: WebGPURenderer): void {
    const dst = this.src ^ 1;

    this.decayMesh.material = this.decayMaterials[this.src]!;
    renderer.setRenderTarget(this.targets[dst]!);
    renderer.render(this.trailScene, this.trailCamera);
    renderer.setRenderTarget(null);

    this.compositeMesh.material = this.compositeMaterials[dst]!;
    this.src = dst;
  }

  /**
   * Under `prefers-reduced-motion` the studio renders a couple of frames and
   * stops the loop, which for a scene whose whole subject is accumulation would
   * leave an empty screen — indistinguishable from the background being broken.
   * So the simulation is run forward in one burst before that single frame is
   * drawn: the result is a still image of the field rather than an animation of
   * it, which is what the preference actually asks for.
   *
   * The warm-up is CPU-only, because the trail buffer only retains about
   * `trailLife` seconds and passes older than that would be decayed away
   * anyway. Only the tail is paid for on the GPU.
   */
  private preroll(renderer: WebGPURenderer): void {
    const dt = 1 / 30;

    /*
     * `update` derives these from the frame delta, and the first frame's delta
     * is zero — which yields a decay of exactly 1.0. Left alone, the pre-roll
     * would accumulate 240 passes with no falloff at all and produce a denser,
     * flatter field than the scene ever actually looks. Set them for the step
     * size the pre-roll is about to use.
     */
    this.uDecay.value = Math.exp(-dt / Math.max(this.settings.trailLife, 0.01));
    this.uFloor.value = this.settings.trailFloor * dt;

    for (let i = 0; i < PREROLL_WARM_STEPS; i++) {
      this.elapsed += dt;
      this.updateEmitter(dt);
      this.advance(dt);
    }

    for (let i = 0; i < PREROLL_TRAIL_STEPS; i++) {
      this.elapsed += dt;
      this.updateEmitter(dt);
      this.advance(dt);
      this.writeStamps();
      this.advanceTrail(renderer);
    }
  }

  setViewportSize(pixelWidth: number, pixelHeight: number): void {
    this.aspect = pixelWidth / Math.max(pixelHeight, 1);
    this.uAspect.value = this.aspect;

    this.trailCamera.left = -this.aspect;
    this.trailCamera.right = this.aspect;
    this.trailCamera.updateProjectionMatrix();

    // Both quads live in world space and must span the frustum exactly, or the
    // buffer is sampled at the wrong scale and trails smear on resize.
    this.decayMesh.scale.set(this.aspect * 2, 2, 1);
    this.compositeMesh.scale.set(this.aspect * 2, 2, 1);

    const ratio = Math.min(window.devicePixelRatio, this.quality === 'mobile' ? 1.5 : 2);
    let width = Math.max(2, Math.round(pixelWidth * ratio * this.trailScale));
    let height = Math.max(2, Math.round(pixelHeight * ratio * this.trailScale));
    const overshoot = Math.max(width, height) / MAX_TRAIL_EDGE;
    if (overshoot > 1) {
      width = Math.max(2, Math.round(width / overshoot));
      height = Math.max(2, Math.round(height / overshoot));
    }

    // Reallocating clears the accumulated trails. Preserving them would mean
    // stretching the buffer across a new aspect ratio, which looks worse than
    // the momentary clear.
    for (const target of this.targets) {
      if (target.width !== width || target.height !== height) target.setSize(width, height);
    }
  }

  onPointerMove(normalizedX: number, normalizedY: number): void {
    if (this.reducedMotion) return;
    this.pointerX = (normalizedX - 0.5) * 2 * this.aspect;
    this.pointerY = (0.5 - normalizedY) * 2;
    this.pointerActive = true;
    this.uPointer.value.set(this.pointerX, this.pointerY);
  }

  private syncTintUniforms(): void {
    const s = this.settings;
    this.uTrailOpacity.value = s.trailOpacity;
    this.uTintColor.value.set(s.tintColor);
    this.uTintGain.value = s.tintGain;
    this.uTintAmount.value = s.tintAmount;
    this.uTintRadius.value = Math.max(s.tintRadius, 1e-3);
  }

  installDevPanel(folder: GUI): void {
    const s = this.settings;
    this.guiFolder = folder;

    const emitter = folder.addFolder('Emitter');
    emitter.add(s, 'spawnRate', 0, 120, 1).name('Rate (per sec)');
    emitter.add(s, 'spawnBand', 0, 1, 0.005).name('Band width');
    emitter.add(s, 'spawnInset', 0, 1, 0.005).name('Inset from edge');
    emitter.add(s, 'clusterScale', 0.2, 8, 0.05).name('Cluster scale');
    emitter.add(s, 'clusterDrift', 0, 2, 0.01).name('Cluster drift');
    emitter.add(s, 'clusterBias', 1, 12, 1).name('Cluster bias');
    emitter.add(s, 'burstAmount', 0, 1, 0.01).name('Burst amount');
    emitter.add(s, 'burstDrift', 0, 2, 0.01).name('Burst drift');
    emitter.addColor(s, 'colorNear').name('Colour near').onChange((v: string) => this.nearColor.set(v));
    emitter.addColor(s, 'colorFar').name('Colour far').onChange((v: string) => this.farColor.set(v));
    emitter.add(s, 'colorShift', 0, 1, 0.01).name('Colour shift');
    emitter.add(s, 'colorScale', 0.1, 6, 0.05).name('Colour scale');

    const field = folder.addFolder('Flow field');
    field.add(s, 'noiseScale', 0.1, 6, 0.01).name('Scale');
    field.add(s, 'noiseDrift', 0, 1, 0.005).name('Drift');
    field.add(s, 'turns', 0.5, 6, 0.1).name('Turns');
    field.add(s, 'windX', -2, 2, 0.01).name('Wind X');
    field.add(s, 'windY', -2, 2, 0.01).name('Wind Y');
    field.add(s, 'force', 0, 4, 0.01).name('Force');
    field.add(s, 'damping', 0.7, 1, 0.001).name('Damping');
    field.add(s, 'maxSpeed', 0.02, 2, 0.01).name('Max speed');
    field.add(s, 'lifetime', 1, 30, 0.5).name('Lifetime (s)');

    const trail = folder.addFolder('Trails');
    trail.add(s, 'stampSize', 0.001, 0.05, 0.001).name('Brush size');
    trail.add(s, 'stampStretch', 0, 4, 0.05).name('Brush stretch');
    trail.add(s, 'stampStrength', 0, 2, 0.01).name('Brush strength');
    trail.addColor(s, 'hotColor').name('Fast colour').onChange((v: string) => this.hotColor.set(v));
    trail.add(s, 'hotMix', 0, 1, 0.01).name('Fast mix');
    trail.add(s, 'trailLife', 0.1, 12, 0.1).name('Trail life (s)');
    trail.add(s, 'trailFloor', 0, 0.05, 0.001).name('Trail floor');
    trail
      .add(s, 'trailOpacity', 0, 2, 0.01)
      .name('Trail opacity')
      .onChange(() => this.syncTintUniforms());
    trail.add({ live: () => this.particles.length }, 'live').name('Particles (live)').disable();

    const pointer = folder.addFolder('Pointer');
    pointer.add(s, 'pointerAttract').name('Attract (orbit)');
    pointer.add(s, 'pointerRadius', 0.05, 2, 0.01).name('Radius');
    pointer.add(s, 'pointerPush', 0, 5, 0.01).name('Strength');
    pointer.add(s, 'orbitSpeed', 0, 4, 0.01).name('Orbit speed');
    pointer.add(s, 'orbitPull', 0, 2, 0.01).name('Orbit pull');
    pointer.addColor(s, 'tintColor').name('Tint colour').onChange(() => this.syncTintUniforms());
    pointer.add(s, 'tintAmount', 0, 1, 0.01).name('Tint amount').onChange(() => this.syncTintUniforms());
    pointer.add(s, 'tintGain', 0, 6, 0.01).name('Tint gain').onChange(() => this.syncTintUniforms());
    pointer.add(s, 'tintRadius', 0.05, 2, 0.01).name('Tint radius').onChange(() => this.syncTintUniforms());

    folder.add({ export: () => this.exportSettings() }, 'export').name('⇩ Export defaults');

    folder.close();
  }

  private exportSettings(): void {
    const name = this.quality === 'mobile' ? 'MOBILE_SETTINGS' : 'DESKTOP_SETTINGS';
    const body = Object.entries(this.settings)
      .map(([k, v]) => `  ${k}: ${typeof v === 'string' ? `'${v}'` : v},`)
      .join('\n');
    const snippet = `const ${name}: DriftSettings = {\n${body}\n};`;

    // eslint-disable-next-line no-console
    console.log(snippet);
    void navigator.clipboard?.writeText(snippet).catch(() => {
      /* clipboard blocked (insecure context / permission) — console copy stands */
    });
  }

  dispose(): void {
    this.guiFolder?.destroy();
    for (const target of this.targets) target.dispose();
    for (const material of this.decayMaterials) material.dispose();
    for (const material of this.compositeMaterials) material.dispose();
    this.decayMesh.geometry.dispose();
    this.compositeMesh.geometry.dispose();
    this.stampMesh.geometry.dispose();
    (this.stampMesh.material as MeshBasicMaterial).dispose();
    this.brush.dispose();
  }
}

function createTrailTarget(): RenderTarget {
  return new RenderTarget(2, 2, {
    type: HalfFloatType,
    depthBuffer: false,
    stencilBuffer: false,
    generateMipmaps: false,
    minFilter: LinearFilter,
    magFilter: LinearFilter,
  });
}
